import React, { useMemo, useState } from "react";
import {
  Package,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../../styles/LoadTypes.css";

const initialLoadTypes = [
  {
    id: 1,
    name: "Full Load",
    description: "Dedicated vehicle for the complete shipment",
    status: "Active",
  },
  {
    id: 2,
    name: "Part Load",
    description: "Share vehicle space with other shipments",
    status: "Active",
  },
  {
    id: 3,
    name: "Shared Load",
    description: "Goods transported together with multiple customers",
    status: "Active",
  },
  {
    id: 4,
    name: "Dedicated Load",
    description: "Exclusive vehicle assigned to a single customer",
    status: "Active",
  },
  {
    id: 5,
    name: "Small Load",
    description: "Suitable for small quantity household goods",
    status: "Active",
  },
  {
    id: 6,
    name: "Heavy Load",
    description: "Suitable for heavy or bulky goods",
    status: "Inactive",
  },
];

export default function LoadTypes() {
  const [loadTypes, setLoadTypes] = useState(initialLoadTypes);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 6;

  const filteredData = useMemo(() => {
    return loadTypes.filter((item) => {
      const searchValue = search.toLowerCase();

      const matchesSearch =
        item.name.toLowerCase().includes(searchValue) ||
        item.description.toLowerCase().includes(searchValue);

      const matchesStatus =
        !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [loadTypes, search, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredData.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = loadTypes.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = loadTypes.filter(
    (item) => item.status === "Inactive"
  ).length;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const toggleStatus = (id) => {
    setLoadTypes((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const deleteLoadType = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this load type?"
    );

    if (!confirmed) return;

    setLoadTypes((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleAdd = () => {
    alert("Add Load Type form will be connected here.");
  };

  const handleEdit = (item) => {
    alert(`Edit Load Type: ${item.name}`);
  };

  return (
    <div className="load-types-page">

      {/* Header */}
      <div className="load-types-header">

        <div className="load-types-title">

          <div className="load-types-icon">
            <Package size={22} />
          </div>

          <div>
            <h1>Load Types</h1>
            <p>
              Manage load types used for moving quotations
            </p>
          </div>

        </div>

        <button
          className="load-add-btn"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add Load Type
        </button>

      </div>

      {/* Summary */}
      <div className="load-summary-grid">

        <div className="load-summary-card">

          <div className="load-summary-icon total">
            <Package size={20} />
          </div>

          <div>
            <span>Total Types</span>
            <strong>{loadTypes.length}</strong>
          </div>

        </div>

        <div className="load-summary-card">

          <div className="load-summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Types</span>
            <strong>{activeCount}</strong>
          </div>

        </div>

        <div className="load-summary-card">

          <div className="load-summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Types</span>
            <strong>{inactiveCount}</strong>
          </div>

        </div>

      </div>

      {/* Filters */}
      <div className="load-filter-card">

        <div className="load-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search load type..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search || statusFilter) && (
          <button
            className="load-clear-btn"
            onClick={clearFilters}
          >
            <X size={15} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="load-table-card">

        <div className="load-table-wrapper">

          <table className="load-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Load Type</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr key={item.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="load-name">

                        <div className="load-row-icon">
                          <Package size={16} />
                        </div>

                        <strong>{item.name}</strong>

                      </div>
                    </td>

                    <td>
                      <span className="load-description">
                        {item.description}
                      </span>
                    </td>

                    <td>

                      <button
                        className={`load-status ${
                          item.status === "Active"
                            ? "active"
                            : "inactive"
                        }`}
                        onClick={() =>
                          toggleStatus(item.id)
                        }
                      >
                        {item.status}
                      </button>

                    </td>

                    <td>

                      <div className="load-actions">

                        <button
                          className="load-action edit"
                          title="Edit"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="load-action delete"
                          title="Delete"
                          onClick={() =>
                            deleteLoadType(item.id)
                          }
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="load-empty"
                  >
                    No load types found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="load-pagination">

          <span>
            Showing{" "}
            {filteredData.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredData.length
            )}{" "}
            of {filteredData.length}
          </span>

          <div className="load-pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) =>
                  Math.max(1, prev - 1)
                )
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPage(pageNumber)
                }
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile Cards */}
      <div className="load-mobile-list">

        {currentRows.length > 0 ? (
          currentRows.map((item) => (
            <div
              className="load-mobile-card"
              key={item.id}
            >

              <div className="load-mobile-top">

                <div className="load-name">

                  <div className="load-row-icon">
                    <Package size={16} />
                  </div>

                  <div>
                    <strong>{item.name}</strong>
                    <span>
                      {item.description}
                    </span>
                  </div>

                </div>

                <button
                  className={`load-status ${
                    item.status === "Active"
                      ? "active"
                      : "inactive"
                  }`}
                  onClick={() =>
                    toggleStatus(item.id)
                  }
                >
                  {item.status}
                </button>

              </div>

              <div className="load-mobile-actions">

                <button
                  className="mobile-load-edit"
                  onClick={() =>
                    handleEdit(item)
                  }
                >
                  <Pencil size={15} />
                  Edit
                </button>

                <button
                  className="mobile-load-delete"
                  onClick={() =>
                    deleteLoadType(item.id)
                  }
                >
                  <Trash2 size={15} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="load-mobile-empty">
            No load types found
          </div>
        )}

      </div>

    </div>
  );
}